import {
    captureError,
    commaSeparate,
    getCurrentGame, getHairColor,
    getRandomChoice,
    getRandomInt,
    getRandomSample, myround, objectInArray,
    toTitleCase,
    weightedChoice
} from "./helpers.js";
import {
    addCompanyChampionships,
    addCompanyCompanyAlerts,
    addCompanyCompanyLogs,
    addCompanyMessages,
    addCompanyNewsItems,
    addCompanyRandomEvents,
    getCompanyChampionships,
    getCompanyEvents,
    getCompanyFeudItems,
    getCompanyGame,
    getCompanyGimmickMatches,
    getCompanyRandomEvents,
    getCompanyStaff
} from "../models/company.js";
import {
    checkObjectHot,
    checkObjectStale,
    createCompanyAlert, getAvailableStaffMembersByRole, getCompanyLatestEvent,
    getCompanyRosterByType, getCompanyUnreadMessages, getRandomStaffMemberByRole,
    updateObjectOverness
} from "./companies.js";
import {createMessage, createStaffMemberUnavailableMessage} from "./messages.js";
import {randomEventsData, superstarUnavailableReasons} from "../data/randomEvents.js";
import {globalUser} from "./globals.js";
import {
    addRandomEventMessages,
    getRandomEventChampionship, getRandomEventGimmickMatch, getRandomEventMessage,
    getRandomEventSuperstar,
    RandomEvent
} from "../models/randomEvent.js";
import {
    addKayfabeInjuryToSuperstar,
    changeSuperstarMoraleToHappy, changeSuperstarMoraleToUnhappy,
    checkAcquireTrait,
    checkAvailability, checkSuperstarWorkThroughInjury, getRankFromPopularity,
    getRosterMemberSuperstars, getSuperstarActiveFactions
} from "./superstars.js";
import {addRosterFeuds, getRosterBrands, getRosterFeuds, getRosterSuperstars} from "../models/roster.js";
import {updateSuperstarWorkrate} from "./processMatch.js";
import {addFeudSuperstars, Feud, getFeudParticipants} from "../models/feud.js";
import {getTagTeamSuperstars} from "../models/tagTeam.js";
import {checkTalentRequestAction} from "./talentRequests.js";
import {CompanyLogs, db, Feuds, RandomEvents} from "../models/models.js";
import {
    addSuperstarChampionships,
    addSuperstarRandomEvents,
    getSuperstarCurrentGimmick,
    getSuperstarGimmicks, getSuperstarTagTeams
} from "../models/superstar.js";
import {addGimmickMatchRandomEvents} from "../models/gimmickMatch.js";
import {addChampionshipRandomEvents, getChampionshipSuperstar} from "../models/championship.js";
import {getRosterActiveFactions, updateFactionXpAndReputation} from "./factions.js";
import {graphicsData} from "../data/graphics.js";
import {CompanyLog} from "../models/companyLog.js";
import {traitsData} from "../data/traits.js";
import {getUniqueChampionships} from "./championships.js";
import {generateNewsItem} from "./news.js";
import {addFeudItemFeuds} from "../models/feudItem.js";
import {getGameCurrentUserCompany, getGameMainUserCompany} from "./games.js";
import {getLatestMonthlyEvent} from "./events.js";

async function getRandomEventData(randomEvent) {
    let data = {};
    data['name'] = randomEvent.name;
    data['choices'] = randomEvent.choices;
    data['uid'] = randomEvent.$loki;

    const message = await getRandomEventMessage(randomEvent);
    if (message) {
        data['subject'] = message.subject;
        data['content'] = message.content;
        data['fromName'] = message.fromName;
    }

    return data;
}

async function addRandomEventChoice({uid, choice} = {}) {
    let data = {};
    const game = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(game);
    const mainCompany = await getGameMainUserCompany(game);

    // Get random event
    const randomEvent = RandomEvents.get(uid);

    // Check for errors
    if (!randomEvent) {
        data['error'] = 'random-event-not-found'
        return data;
    }
    if (!randomEvent.choices.includes(choice)) {
        data['error'] = 'choice-not-found'
        return data;
    }
    if (randomEvent.selectedChoice) {
        data['error'] = 'choice-already-added'
        return data;
    }

    // Add choice
    randomEvent.selectedChoice = choice;

    // Process immediate events
    if (randomEvent.name === 'drugAbuse' && choice === 'Suspend') {
        await processDrugAbuseEvent(randomEvent, userCompany)
    }
    if (randomEvent.name === 'drugScandal' && choice === 'Suspend') {
        await processDrugScandalEvent(randomEvent, userCompany)
    }
    if (randomEvent.name === 'superstarBreak' && choice === 'Decline') {
        await processSuperstarBreakEvent(randomEvent, userCompany)
    }
    if (randomEvent.name === 'backstageBrawl' && choice === 'Suspend') {
        await processBackstageBrawlEvent(randomEvent, userCompany)
    }
    if (randomEvent.name === 'conservativeGovernmentShow' && choice === 'Accept') {
        // Add news
        let newsItem = await generateNewsItem({
            company: userCompany, month: game.monthCount, year: game.year, newsType: 'conservative_government_show',
            location: randomEvent.location,
        });
        await addCompanyNewsItems(mainCompany, [newsItem]);
    }

    db.saveDatabase();

    // Add reply if message from owner or staff
    const randomEventMessage = await getRandomEventMessage(randomEvent);
    if (['owner', 'staff'].includes(randomEventMessage.tag)) {
        const replies = ['Got it.', 'Understood.', 'All right.'];
        data['replyContent'] = getRandomChoice(replies);
    }

    // Get unread messages count
    const unreadMessages = await getCompanyUnreadMessages(userCompany);
    data['unreadMessages'] = unreadMessages.length;

    return data;
}

async function checkForStaffRandomEvent(staffMembers, company) {  // check_for_staff_random_event
	let game = await getCurrentGame();
	let pick = getRandomInt(0, 1000);
	let staffMember = getRandomChoice(staffMembers);
	let data = {};

	// Select random event
	if (pick <= 50) {  // once every 20 months
		let eventTypePick = getRandomInt(0, 100);
		if (eventTypePick <= 10) {
			data['name'] = 'court-case';
			staffMember.unavailableMonths = getRandomInt(4, 8);
		} else if (eventTypePick <= 30) {
			data['name'] = 'family-issue';
			staffMember.unavailableMonths = 2;
		} else if (eventTypePick <= 60) {
			data['name'] = 'illness';
			staffMember.unavailableMonths = 1;
		}
	}

	// Add message and company alert
	if (data['name']) {
        let newMessage = await createStaffMemberUnavailableMessage(staffMember, data, game.monthCount, game.year);
        await addCompanyMessages(company, [newMessage]);

        let newCompanyAlert = await createCompanyAlert('randomEvent', toTitleCase(data['name'].replace('-', ' ')));
        await addCompanyCompanyAlerts(company, [newCompanyAlert]);
	}

	return data;
}

async function randomEventCheck(company, weeklyShow) {
    const data = {};

	// Process existing random events
    const companyRandomEvents = (await getCompanyRandomEvents(company)).filter(x => x.monthsRemaining > 0);
    data['processedEvents'] = await processRandomEvents(companyRandomEvents, company, weeklyShow);

	// Run new ones if no existing ones present and after monthly event
    if (!companyRandomEvents.length && !weeklyShow) {
        data['ranEvents'] = await runRandomEvents(company);
    }
    return data;
}

async function runRandomEvents(company) {
    const ranEvents = [];
    // Chance to run a random event
    const randomEventName = await selectRandomEventName(company);
    if (randomEventName && !company.storyName) {
        const randomEventRan = await runRandomEvent(randomEventName, company);
        if (randomEventRan) {
            ranEvents.push(randomEventName);
			const newCompanyAlert = await createCompanyAlert('randomEvent',
				`${toTitleCase(randomEventName.replace(/([A-Z])/g, " $1"))}`);  // camel case to title case
            await addCompanyCompanyAlerts(company, [newCompanyAlert]);
        }
    }
    return ranEvents;
}

async function selectRandomEventName(company) {
    // Select category or skip selection
	const pick = getRandomInt(1, 100);
	let filteredRandomEventsData = [];
	if (pick <= 6 && globalUser.paid) {
		// Major
		filteredRandomEventsData = randomEventsData.filter(x => x['category'] === 'major');
	}
	else if (pick <= 31) {
		// Minor
		filteredRandomEventsData = randomEventsData.filter(x => x['category'] === 'minor');
	}

	// Select random event
	if (filteredRandomEventsData.length) {
		const companyRandomEvents = (await getCompanyRandomEvents(company)).reverse();
		filteredRandomEventsData = filteredRandomEventsData.filter(x => filterRandomEventSelections(x,
			companyRandomEvents));
		if (filteredRandomEventsData.length) {
			const randomEvent = getRandomChoice(filteredRandomEventsData);
			return randomEvent['name'];
		}
	}

	return false;
}

function filterRandomEventSelections(randomEventData, companyRandomEvents) {
	// Don't run if held within last x random events. Higher limit for non-repeatable and major events.
	let limit;
	if (randomEventData['category'] === 'minor') {
		limit = 10;
		if (randomEventData['repeatable']) {
			limit = 4;
		}
	} else {
		limit = 20;
		if (randomEventData['repeatable']) {
			limit = 8;
		}
	}
	const previousRandomEventNames = companyRandomEvents.slice(0, limit).map(x => x.name);
	return !previousRandomEventNames.includes(randomEventData['name']);
}

async function runRandomEvent(randomEventName, company) {
    // Dictionary of functions
    const randomEventFunctions = {
        // Minor
        'superstarNotAvailable': superstarNotAvailableEventFunction,
        'mainstreamCoverage': mainstreamCoverageEventFunction,
        'ownerControversy': ownerControversyEventFunction,
        'negativeSuperstarCoverage': negativeSuperstarCoverageEventFunction,
        'moraleBoost': moraleBoostEventFunction,
        'gimmickInterview': gimmickInterviewEventFunction,
        'goodTrainingSession': goodTrainingSessionEventFunction,
        'neglectedTraining': neglectedTrainingEventFunction,
        'hairChange': hairChangeEventFunction,
        'gimmickMatchBoost': gimmickMatchBoostEventFunction,
        'feudIgnored': feudIgnoredEventFunction,
        'feudBoosted': feudBoostedEventFunction,
        'cultFollowing': cultFollowingEventFunction,
        'wrestlerCourt': wrestlerCourtEventFunction,
        'factionBoosted': factionBoostedEventFunction,
        'factionIgnored': factionIgnoredEventFunction,
        // Major
        'fluBug': fluBugEventFunction,
        'flightsCancelled': flightsCancelledEventFunction,
        'championshipStolen': championshipStolenEventFunction,
        'backstageUnrest': backstageUnrestEventFunction,
        'conservativeGovernmentShow': conservativeGovernmentShowEventFunction,
        'planeRideSuspensions': planeRideSuspensionsEventFunction,
        'stormAffectsShow': stormAffectsShowEventFunction,
        'showAppearances': showAppearancesEventFunction,
        'drugAbuse': drugAbuseEventFunction,
        'backstageFight': backstageFightEventFunction,
        'superstarBreak': superstarBreakEventFunction,
        'drugScandal': drugScandalEventFunction,
        'backstageBrawl': backstageBrawlEventFunction,
    };

	// Get function and run
    const randomEventFunction = randomEventFunctions[randomEventName];
    if (randomEventFunction) {
        return randomEventFunction(company);
    }

    return null;
}

async function processRandomEvents(randomEvents, company, weeklyShow) {
    const processedRandomEvents = [];
    for (const randomEvent of randomEvents) {
        if (!weeklyShow) {
            randomEvent.monthsRemaining -= 1;
        }
        const randomEventProcessed = await processRandomEvent(randomEvent, company, weeklyShow);
        if (randomEventProcessed) {
            processedRandomEvents.push(randomEvent.name);
        }
    }
    return processedRandomEvents;
}

async function processRandomEvent(randomEvent, company, weeklyShow) {
    // Dictionary of functions
    const randomEventFunctions = {
        'championshipStolen': processChampionshipStolenEvent,
        'gimmickMatchBoost': processGimmickMatchBoostEvent,
        'conservativeGovernmentShow': processConservativeGovernmentShowEvent,
        'womenOnlyShow': processWomenOnlyShowEvent,
        'drugAbuse': processDrugAbuseEvent,
        'superstarBreak': processSuperstarBreakEvent,
        'cultFollowing': processCultFollowingEvent,
        'drugScandal': processDrugScandalEvent,
        'backstageBrawl': processBackstageBrawlEvent,
    };

	// Get function and run
	const randomEventFunction = randomEventFunctions[randomEvent.name];
    if (randomEventFunction) {
        return randomEventFunction(randomEvent, company, weeklyShow);
    }

	return null;
}

async function getActiveSuperstars(roster) {
	return (await getRosterSuperstars(roster)).filter(x => checkAvailability(x) &&
        !['retirement_return', 'traded'].includes(x.activeEffect));
}

async function getCompanyTalentManager(company) {
	return getRandomStaffMemberByRole(await getCompanyStaff(company), 'talent_manager');
}

function setSuperstarSkillLevelForDecrease(s, skill) {
    const xps = {3: 300, 2: 100, 1: 0};
    if (skill === 'workrate') {
        if (s.workrateXp < (xps[s.workrate] || 0)) {
            s.workrate -= 1
        }
        return s.workrate;
    }
    if (skill === 'charisma') {
        if (s.charismaXp < (xps[s.charisma] || 0)) {
            s.charisma -= 1
        }
        return s.charisma;
    }
}

/*
Run random event functions
 */

async function superstarNotAvailableEventFunction(company) {  // superstar_not_available_event
    const game = await getCompanyGame(company);

    // Select active superstar
    const roster = await getCompanyRosterByType(company, 'main');
    const activeSuperstars = await getActiveSuperstars(roster);
    const selectedSuperstar = getRandomChoice(activeSuperstars);

    // Make superstar unavailable, select reason
    selectedSuperstar.unavailable = true;
    const reason = getRandomChoice(superstarUnavailableReasons);

    // Create message from superstar
	const messageContent = `<p>${reason['content']}</p>`;
    const newMessage = await createMessage(reason['name'], selectedSuperstar.name, game.monthCount, game.year,
	    messageContent, 'superstar');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
	newRandomEvent.name = 'superstarNotAvailable';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function mainstreamCoverageEventFunction(company) {
    const game = await getCompanyGame(company);
    const mediaNames = ['news channel', 'entertainment show', 'magazine', 'tabloid', 'media network'];
    const roster = await getCompanyRosterByType(company, 'main');
    const rosterBrands = await getRosterBrands(roster);

    // Select brand or company
    let objectToBoost;
    let objectName;
    if (rosterBrands.length && getRandomInt(0, 100) <= 50) {
        objectToBoost = getRandomChoice(rosterBrands);
        objectName = objectToBoost.name;
    } else {
        objectToBoost = company;
        objectName = company.name;
    }

    // Add to overness
    const overnessChange = getRandomInt(5, 10);
    updateObjectOverness(objectToBoost, overnessChange);
    checkObjectHot(objectToBoost, overnessChange);
    checkObjectStale(objectToBoost, overnessChange);

    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="green margin-right-5"><i class="fa fa-angle-double-up"></i> ${overnessChange}%</span>` +
        `Overness</div>`;
    const subject = 'Mainstream Coverage';
    const content = `<p>A popular ${getRandomChoice(mediaNames)} covered ${objectName} recently, resulting in a ` +
        `slight boost to overness.</p><p>${changeHtml}</p>`;

    // Create message from owner
    const fromName = company.ownerName || 'Owner';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'mainstreamCoverage';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function ownerControversyEventFunction(company) {
    const game = await getCompanyGame(company);

    // Decrease fans
    const decrease = Math.abs(Math.floor(company.fans * 0.05));
    company.fans -= decrease;
    const ownerName = company.ownerName || `the Owner of ${company.name}`;

    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="red margin-right-5"><i class="fa fa-angle-double-down"></i> ${decrease.toLocaleString('en')}</span>` +
        `Fans </div>`;
    const subject = 'Controversy';
    const content = `<p>In a recent interview, ${ownerName} took a controversial political position, offending many ` +
        `viewers. As a result, we've noticed a slight decrease in our fanbase.</p><p>${changeHtml}</p>`;

    // Create message
    const talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'ownerControversy';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function negativeSuperstarCoverageEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select active superstar
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);
    const superstar = getRandomChoice(superstars);

    // Decrease pop
    const decrease = myround(getRandomInt(50, 100), 10);
    superstar.popularity -= decrease;
    if (superstar.popularity < 0) {
        superstar.popularity = 0;
    }

    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="red margin-right-5"><i class="fa fa-angle-double-down"></i> ${decrease}</span>` +
        `Popularity</div>`;
    const subject = 'Insensitive Comments';
    const content = `<p>${superstar.name} made some insensitive comments in a recent interview, drawing the ire ` +
        `of many fans. Other media joined in on the story and as a result, ${superstar.name}'s popularity has ` +
        `suffered.</p><p>${changeHtml}</p>`;

    // Create message
    const talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'negativeSuperstarCoverage';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function moraleBoostEventFunction(company) {
    const game = await getCompanyGame(company);
    const activities = ['drinks', 'bowling', 'a walk', 'a trip to the amusement park', 'karaoke'];

    // Select unhappy, active superstar
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);
    const unhappySuperstars = superstars.filter(x => x.unhappy);
    if (!unhappySuperstars.length) {
        return null;
    }
    const superstar = getRandomChoice(unhappySuperstars);

    // Select happy, active superstar
    const happySuperstars = superstars.filter(x => !x.unhappy);
    if (!happySuperstars.length) {
        return null;
    }

    // Find superstar with leader trait
    let leaderTraitSuperstars = [];
    let moraleBoostHelperChance = 0;
    for (let s of happySuperstars) {
        for (const trait of s.traits) {
            const traitData = traitsData[trait];
            if (traitData && traitData['morale_boost_helper_chance']) {
                leaderTraitSuperstars.push(s);
                moraleBoostHelperChance = traitData['morale_boost_helper_chance'];
            }
        }
    }

    let moraleBoosterSuperstar;
    if (leaderTraitSuperstars.length && getRandomInt(0, 100) <= moraleBoostHelperChance) {
         moraleBoosterSuperstar = getRandomChoice(leaderTraitSuperstars)
    }
    else {
         moraleBoosterSuperstar = getRandomChoice(happySuperstars)
    }

    // Change morale
    changeSuperstarMoraleToHappy(superstar);

    // Create message
    const subject = 'Morale Boost';
    const content = `<p>I hope you don't mind, but I took out ${superstar.name} for ${getRandomChoice(activities)} ` +
        `last night. ${toTitleCase(superstar.pronoun)} seemed quite upset, so I thought I'd cheer ${superstar.objectivePronoun} up.</p>` +
        `<p>We ended up having a real good talk, and I don't think ${superstar.pronoun} will give you any problems in ` +
        `the near future.</p>`;
    const fromName = moraleBoosterSuperstar.name;
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'superstar');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'moraleBoost';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    // Check to acquire trait
    await checkAcquireTrait(moraleBoosterSuperstar, 'Leader');

    return true;
}

async function gimmickInterviewEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select active superstar
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = [];
    for (let x of await getActiveSuperstars(roster)) {
        if ((await getSuperstarGimmicks(x)).length && !x.debutNewGimmick) {
            superstars.push(x)
        }
    }
    if (!superstars.length) {
        return null;
    }

    // Find superstar with character worker trait
    let characterWorkerTraitSuperstars = [];
    let gimmickInterviewSuperstarChance = 0;
    for (let s of superstars) {
        for (const trait of s.traits) {
            const traitData = traitsData[trait];
            if (traitData && traitData['gimmick_interview_superstar_chance']) {
                characterWorkerTraitSuperstars.push(s);
                gimmickInterviewSuperstarChance = traitData['gimmick_interview_superstar_chance'];
            }
        }
    }

    let superstar;
    if (characterWorkerTraitSuperstars.length && getRandomInt(0, 100) <= gimmickInterviewSuperstarChance) {
         superstar = getRandomChoice(characterWorkerTraitSuperstars)
    }
    else {
         superstar = getRandomChoice(superstars)
    }

    // Increase gimmick effectiveness
    const superstarGimmick = await getSuperstarCurrentGimmick(superstar);
    const increase = myround(getRandomInt(15, 30), 5);
    superstarGimmick.effectiveness += increase;
    if (superstarGimmick.effectiveness > 100) {
        superstarGimmick.effectiveness = 100;
    }

    // Create message
    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="green margin-right-5"><i class="fa fa-angle-double-up"></i> ${increase}%</span>` +
        `Effectiveness</div>`;
    const subject = 'Gimmick Boost';
    const content = `<p>${superstar.name} gave a remarkable interview a few days ago, done fully in character. ` +
        `This gave ${superstar.possessivePronoun} gimmick a good boost in the eyes of the fans.</p>` +
        `<p>${changeHtml}</p>`;
    const talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'gimmickInterview';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function goodTrainingSessionEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select trained superstar
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);
    const lowWorkrateSuperstars = superstars.filter(x => x.workrate < 3);
    if (!lowWorkrateSuperstars.length) {
        return null;
    }
    const superstar = getRandomChoice(lowWorkrateSuperstars);

    // Select training from
    const highWorkrateSuperstars = superstars.filter(x => x.workrate >= 3);
    if (!highWorkrateSuperstars.length) {
        return null;
    }
    const trainingFrom = getRandomChoice(highWorkrateSuperstars);

    // Increase workrate xp
    const increase = myround(getRandomInt(20, 40), 5);
    superstar.workrateXp += increase;
    await updateSuperstarWorkrate(superstar, company, await getCompanyStaff(company), game.monthCount, game.year);

    // Create message
    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="green margin-right-5"><i class="fa fa-angle-double-up"></i> ${increase}</span>` +
        `Workrate XP</div>`;
    const subject = 'Good Training';
    const content = `<p>I took out ${superstar.name} for training yesterday, and we had a monster of a session. ` +
        `${toTitleCase(superstar.pronoun)} picked up a lot and worked really hard. I could feel ` +
        `${superstar.possessivePronoun} workrate improving.</p>` +
        `<p>${changeHtml}</p>`;
    const fromName = trainingFrom.name;
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'superstar');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'goodTrainingSession';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function neglectedTrainingEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select active superstar
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);

    // Find superstars with hates training trait
    let hatesTrainingTraitSuperstars = [];
    let neglectedTrainingSuperstarChance = 0;
    for (let s of superstars) {
        for (const trait of s.traits) {
            const traitData = traitsData[trait];
            if (traitData && traitData['neglected_training_superstar_chance']) {
                hatesTrainingTraitSuperstars.push(s);
                neglectedTrainingSuperstarChance = traitData['neglected_training_superstar_chance'];
            }
        }
    }

    // Get superstar
    let superstar;
    if (hatesTrainingTraitSuperstars.length && getRandomInt(0, 100) <= neglectedTrainingSuperstarChance) {
        superstar = getRandomChoice(hatesTrainingTraitSuperstars)
    }
    else {
        const weights = superstars.map(x => {
            const weight = x.fatigue >= 50 ? Math.pow(x.fatigue / 20, 3) : 0;
            return [x, weight];
        });
        superstar = weightedChoice(weights);
    }

    // Decrease workrate xp
    const decrease = myround(getRandomInt(20, 40), 5);
    superstar.workrateXp -= decrease;
    if (superstar.workrateXp < 0) {
        superstar.workrateXp = 0;
    }
    setSuperstarSkillLevelForDecrease(superstar, 'workrate');

    // Create message
    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="red margin-right-5"><i class="fa fa-angle-double-down"></i> ${decrease}</span>` +
        `Workrate XP</div>`;
    const subject = 'No Training';
    const content = `<p>I've been noticing that ${superstar.name} hasn't showed up in the gym for a while. ` +
        `${toTitleCase(superstar.pronoun)} is refusing to train, and I feel like ` +
        `${superstar.possessivePronoun} workrate is decreasing.</p>` +
        `<p>${changeHtml}</p>`;
    const trainer = getRandomStaffMemberByRole(await getCompanyStaff(company), 'trainer');
    const fromName = trainer ? trainer.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'neglectedTraining';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function hairChangeEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select bald superstar
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);
    const baldSuperstars = superstars.filter(x => {
        const graphic = x.graphic;
        return graphic && Object.keys(graphic).length !== 0 && !graphic['empty_graphic'] &&
            graphic['hair']['object'] === 0;
    });
    if (!baldSuperstars.length) {
        return null;
    }
    const superstar = getRandomChoice(baldSuperstars);
    const graphic = superstar.graphic;

    // Select hair object to add
    const hairObjects = graphicsData['hairs'].filter(x => x['category'] === 'short');
    const hairObject = getRandomChoice(hairObjects);
    const hairObjectNumber = hairObject['number'];

    // Select hair color
    const regularColors = ['0x443526', '0x222222'];
    let hairColor = null;
    // Get hair color from beard color if any
    if (graphic['facialHair']['object'] > 0) {
        hairColor = graphic['facialHair']['colors'][0];
    }
    // Otherwise get a new hair color
    if (!hairColor) {
        if (getRandomInt(0, 100) <= 10) {
            hairColor = getHairColor();
        } else {
            hairColor = getRandomChoice(regularColors);
        }
    }

    // Add hair colors for each design in hair object
    const hairColors = new Array(hairObject['designs']).fill(hairColor);

    // Add hair
    graphic['hair']['object'] = hairObjectNumber;
    graphic['hair']['colors'] = hairColors;

    // Add hair on alternate attires
    const alternateAttires = graphic['alternate_attires'];
    if (alternateAttires) {
        const newAlternateAttires = [];
        for (const alt of alternateAttires) {
            let altGraphic = JSON.parse(JSON.stringify(alt));
            altGraphic['hair']['object'] = hairObjectNumber;
            altGraphic['hair']['colors'] = hairColors;
            newAlternateAttires.push(altGraphic);
        }
        graphic['alternate_attires'] = newAlternateAttires;
    }

    superstar.graphic = graphic;

    // Create message
    const subject = 'Hair Change';
    const content = "<p>I've decided to grow out my hair. It was time for a change. What do you think?</p>";
    const fromName = superstar.name;
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'superstar');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'hairChange';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function gimmickMatchBoostEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select gimmick match
    const gimmickMatches = await getCompanyGimmickMatches(company);
    if (!gimmickMatches.length) {
        return null;
    }
    const gimmickMatch = getRandomChoice(gimmickMatches);

    // Add boost
    gimmickMatch.ratingBoost += 0.2;

    // Create message
    const subject = 'Match Hype';
    const content = `<p>${gimmickMatch.name} Matches are quite hot these days. Fans are giving them a lot of attention ` +
        `and I think we could get quite a bit of hype if we use them at the next show.</p>`;
    const fromName = company.ownerName || 'Owner';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'gimmickMatchBoost';
    newRandomEvent.monthsRemaining = 1;
    await addCompanyRandomEvents(company, [newRandomEvent]);
    await addGimmickMatchRandomEvents(gimmickMatch, [newRandomEvent]);

    return true;
}

async function feudIgnoredEventFunction(company) {
    const game = await getCompanyGame(company);
    const actions = ['training at the local gym', 'drinking at a club', 'attending a sporting event',
        'sitting at a movie premiere', 'attending a concert'];

    // Select feud
    const roster = await getCompanyRosterByType(company, 'main');
    const feuds = (await getRosterFeuds(roster)).filter(x => x.started && !x.ended);
    if (!feuds.length) {
        return null;
    }

    // Find feud with neglects story trait participant superstar
    let neglectsStoryTraitFeuds = [];
    let feudIgnoredFeudChance = 0;
    for (let feud of feuds) {
        for (let participant of await getFeudParticipants(feud)) {
            for (let s of await getRosterMemberSuperstars(participant)) {
                for (let trait of s.traits) {
                    let traitData = traitsData[trait];
                    if (traitData && traitData['feud_ignored_feud_chance']) {
                        neglectsStoryTraitFeuds.push(feud);
                        feudIgnoredFeudChance = traitData['feud_ignored_feud_chance'];
                    }
                }
            }
        }
    }

    let feud;
    if (neglectsStoryTraitFeuds.length && getRandomInt(0, 100) <= feudIgnoredFeudChance) {
        feud = getRandomChoice(neglectsStoryTraitFeuds);
    }
    else {
        feud = getRandomChoice(feuds);
    }

    // Select superstars
    const feudParticipants = await getFeudParticipants(feud);
    if (feudParticipants.length < 2) {
        return null;
    }

    let selectedSuperstars;
    if (feudParticipants[0].objectName === 'tagTeam') {
        // Get one superstar from each tag team
        let firstFeudParticipantSuperstars = await getTagTeamSuperstars(feudParticipants[0]);
        let secondFeudParticipantSuperstars = await getTagTeamSuperstars(feudParticipants[1]);
        if (!firstFeudParticipantSuperstars.length || !secondFeudParticipantSuperstars.length) {
            return null;
        }
        selectedSuperstars = [getRandomChoice(firstFeudParticipantSuperstars),
            getRandomChoice(secondFeudParticipantSuperstars)];
    } else {
        selectedSuperstars = getRandomSample(feudParticipants, 2);
    }

    // Decrease feud hype
    const decrease = myround(getRandomInt(15, 25), 5);
    feud.hype -= decrease;
    if (feud.hype < 0) {
        feud.hype = 0;
    }

    // Create message
    const superstarNames = commaSeparate(selectedSuperstars.map(x => x.name));
    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="red margin-right-5"><i class="fa fa-angle-double-down"></i> ${decrease}</span>` +
        `Hype` +
        `</div>`;
    const subject = 'Feud Ignored';
    const content = `<p>${superstarNames} were recently spotted by fans ${getRandomChoice(actions)} together.</p>` +
        `<p>Obviously, this negatively affected their ongoing Feud's Hype. I've talked to both of them and told them ` +
        `of the consequences of this action.</p>` +
        `<p>${changeHtml}</p>`;
    const talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'feudIgnored';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function feudBoostedEventFunction(company) {
    const game = await getCompanyGame(company);
    const actions = [
        'going to blows on a TV show', 'fighting in front of fans in the parking lot before the show',
        'starting a brawl at a press event', 'yelling at each other in a charity event',
        'refusing to fly on the same flight'
    ];

    // Select feud
    const roster = await getCompanyRosterByType(company, 'main');
    const feuds = (await getRosterFeuds(roster)).filter(x => x.started && !x.ended);
    if (!feuds.length) {
        return null;
    }

    // Find feud with storyteller trait participant superstar
    let storytellerTraitFeuds = [];
    let feudBoostedFeudChance = 0;
    for (let feud of feuds) {
        for (let participant of await getFeudParticipants(feud)) {
            for (let s of await getRosterMemberSuperstars(participant)) {
                for (let trait of s.traits) {
                    let traitData = traitsData[trait];
                    if (traitData && traitData['feud_boosted_feud_chance']) {
                        storytellerTraitFeuds.push(feud);
                        feudBoostedFeudChance = traitData['feud_boosted_feud_chance'];
                    }
                }
            }
        }
    }

    let feud;
    if (storytellerTraitFeuds.length && getRandomInt(0, 100) <= feudBoostedFeudChance) {
        feud = getRandomChoice(storytellerTraitFeuds);
    }
    else {
        feud = getRandomChoice(feuds);
    }

    // Select superstars
    const feudParticipants = await getFeudParticipants(feud);
    if (feudParticipants.length < 2) {
        return null;
    }

    let selectedSuperstars;
    if (feudParticipants[0].objectName === 'tagTeam') {
        // Get one superstar from each tag team
        let firstFeudParticipantSuperstars = await getTagTeamSuperstars(feudParticipants[0]);
        let secondFeudParticipantSuperstars = await getTagTeamSuperstars(feudParticipants[1]);
        if (!firstFeudParticipantSuperstars.length || !secondFeudParticipantSuperstars.length) {
            return null;
        }
        selectedSuperstars = [getRandomChoice(firstFeudParticipantSuperstars),
            getRandomChoice(secondFeudParticipantSuperstars)];
    } else {
        selectedSuperstars = getRandomSample(feudParticipants, 2);
    }

    // Increase feud hype
    const increase = myround(getRandomInt(15, 25), 5);
    feud.hype += increase;

    // Create message
    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="green margin-right-5"><i class="fa fa-angle-double-up"></i> ${increase}</span>` +
        `Hype` +
        `</div>`;
    const subject = 'Feud Boosted';
    const superstarNames = commaSeparate(selectedSuperstars.map(superstar => superstar.name));
    const content = `<p>${superstarNames} recently made a scene by ${getRandomChoice(actions)}.</p>` +
    `<p>The interaction gave a good boost to their ongoing Feud's Hype in the eyes of the fans.</p>` +
    `<p>${changeHtml}</p>`;
    const talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'feudBoosted';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function cultFollowingEventFunction(company) {
    const game = await getCompanyGame(company);
    const attributes = ['cool look', 'witty humor', 'unique personality', 'awesome moves', 'everyman nature',
        'carefree attitude'];

    // Select a superstar from the active roster that is not hot
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = (await getActiveSuperstars(roster)).filter(x => x.overness <= 60);
    if (!superstars.length) {
        return null;
    }
    const superstar = getRandomChoice(superstars);

    // Add to overness
    const increase = getRandomInt(10, 15);
    superstar.overness += increase;
    if (superstar.overness > 100) {
        superstar.overness = 100;
    }

    // Create message
    const changeHtml = `<div class="message-info-box grey">` +
        `<span class="green margin-right-5"><i class="fa fa-angle-double-up"></i> ${increase}%</span>` +
        `Overness</div>`;
    const subject = 'Cult Following';
    const content = `<p>For no discernible reason, a cult following has developed around ${superstar.name} due to ` +
        `${superstar.possessivePronoun} ${getRandomChoice(attributes)}.</p>` +
        `<p>Whether this grows or fades is yet to be seen, but ${superstar.name} got a slight boost ` +
        `to ${superstar.possessivePronoun} overness because of it.</p>` +
        `<p>${changeHtml}</p>`;
    const talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'cultFollowing';
    newRandomEvent.monthsRemaining = 2;
    await addCompanyRandomEvents(company, [newRandomEvent]);
    await addSuperstarRandomEvents(superstar, [newRandomEvent]);

    return true;
}

async function wrestlerCourtEventFunction(company) {
    const game = await getCompanyGame(company);
    const punishments = ['a month-long ban from the locker room', 'pay the (huge) bar tab',
        'bring video games for everyone', 'buy everyone food'];

    // Get active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = await getActiveSuperstars(roster);
    if (superstars.length < 5) { // 2 trial superstars, 3 judges
        return null;
    }

    // Find superstars with leader trait
    const leaderTraitSuperstars = [];
    for (let s of superstars) {
        for (const trait of s.traits) {
            const traitData = traitsData[trait];
            if (traitData && traitData['wrestler_court_judge_chance']) {
                leaderTraitSuperstars.push(s);
            }
        }
    }

    // Find superstars with troublemaker trait
    const troublemakerTraitSuperstars = [];
    let wrestlerCourtDefendantChance = 0;
    for (let s of superstars) {
        for (const trait of s.traits) {
            const traitData = traitsData[trait];
            if (traitData && traitData['wrestler_court_defendant_chance']) {
                troublemakerTraitSuperstars.push(s);
                wrestlerCourtDefendantChance = traitData['wrestler_court_defendant_chance']
            }
        }
    }

    // Get defendant and plaintiff
    let defendant;
    if (troublemakerTraitSuperstars.length && getRandomInt(0, 100) <= wrestlerCourtDefendantChance) {
         defendant = getRandomChoice(troublemakerTraitSuperstars)
    }
    else {
         defendant = getRandomChoice(superstars)
    }
    superstars = superstars.filter(x => x.$loki !== defendant.$loki);
    const plaintiff = getRandomChoice(superstars);
    superstars = superstars.filter(x => x.$loki !== plaintiff.$loki);

    // Select judges
    const sortedSuperstars = [...superstars].sort((a, b) => {
        return (objectInArray(b, leaderTraitSuperstars) - objectInArray(a, leaderTraitSuperstars)) || (b.age - a.age);
    });
    const veterans = sortedSuperstars.slice(0, 4);
    const judges = getRandomSample(veterans, 3);

    // Make defendant unhappy
    if (!game.settings['no_unhappiness']) {
        await changeSuperstarMoraleToUnhappy(defendant, company);
    }

    // Create message
    const subject = 'Wrestlers Court';
    const crimes = [`complaining about ${defendant.possessivePronoun} position in the company`,
        'leaving trash in the locker room', 'no-selling moves in the ring', 'speaking of the show in a negative light',
        'trying to bribe the management', 'stealing a wrestling move'];
    const content = `<p>A session of the Wrestlers Court took place recently, with ${plaintiff.name} ` +
        `accusing ${defendant.name} of ${getRandomChoice(crimes)}.</p>` +
        `<p>${commaSeparate(judges.map(x => x.name))} were the judges, and after a lengthy session of ` +
        `pleading cases, ${defendant.name} was found guilty and sentenced to ${getRandomChoice(punishments)}.</p>` +
        `<p>Due to the embarrassment of the whole ordeal, ${defendant.name} isn't too happy.</p>`;
    const talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'wrestlerCourt';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    // Check to acquire trait
    for (let judge of judges) {
        await checkAcquireTrait(judge, 'Leader');
    }

    return true;
}

async function drugAbuseEventFunction(company) {
    const game = await getCompanyGame(company);

    // Get active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);

    // Find superstars with drug habit trait
    let drugHabitTraitSuperstars = [];
    let drugAbuseSuperstarChance = 0;
    for (let superstar of superstars) {
        for (let trait of superstar.traits) {
            let traitData = traitsData[trait];
            if (traitData && traitData['drug_abuse_superstar_chance']) {
                drugHabitTraitSuperstars.push(superstar);
                drugAbuseSuperstarChance = traitData['drug_abuse_superstar_chance'];
            }
        }
    }

    let superstar;
    if (drugHabitTraitSuperstars.length && getRandomInt(0, 100) <= drugAbuseSuperstarChance) {
        superstar = getRandomChoice(drugHabitTraitSuperstars);
    }
    else {
        const weights = superstars.map(x => {
            const weight = x.fatigue >= 50 ? Math.pow(x.fatigue / 20, 3) : 0;
            return [x, weight];
        });
        superstar = weightedChoice(weights);
    }

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'drugAbuse';
    newRandomEvent.monthsRemaining = 1;
    newRandomEvent.choices = ['Suspend', 'Ignore'];
    await addCompanyRandomEvents(company, [newRandomEvent]);
    await addSuperstarRandomEvents(superstar, [newRandomEvent]);

    // Create message for random event
    const subject = 'Drug Abuse';
    const content = `<p>Last night, I caught ${superstar.name} using illicit drugs.</p>` +
        `<p>You have two options here: you can either suspend ${superstar.objectivePronoun} for a couple months and ` +
        `put an immediate stop to the drug use or you can ignore the issue.</p>` +
        `<p>If you don't do anything, the drug use will probably continue and negatively affect ` +
        `${superstar.possessivePronoun} skills.</p>`;
    const talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    newMessage.promptAction = true;
    await addRandomEventMessages(newRandomEvent, [newMessage]);

    // Check to acquire trait
    await checkAcquireTrait(superstar, 'Drug Habit');

    return true;
}

async function factionBoostedEventFunction(company) {
    const game = await getCompanyGame(company);
    let actions = ['meet and greet', 'press event', 'training session', 'charity event', 'merchandise showcase',
                   'photo shoot', 'commercial shoot'];

    // Select faction
    const roster = await getCompanyRosterByType(company, 'main');
    let factions = await getRosterActiveFactions(roster);
    if (!factions.length) {
        return null;
    }
    let faction = getRandomChoice(factions);

    // Increase faction xp
    let increase = myround(getRandomInt(20, 40), 5);
    updateFactionXpAndReputation(faction, increase);

    // Create message
    let changeHtml = `<div class="message-info-box grey">` +
        `<span class="green margin-right-5"><i class="fa fa-angle-double-up"></i> ${increase}</span>XP` +
        `</div>`;
    let subject = "Faction Together";
    let content = `<p>${faction.name} were recently seen attending a ${getRandomChoice(actions)} as a group.</p>` +
        `<p>They arrived together, dressed similarly and acted like a unit. This should give their faction's ` +
        `reputation a boost.</p>` +
        `<p>${changeHtml}</p>`;
    let talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'factionBoosted';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function factionIgnoredEventFunction(company) {
    const game = await getCompanyGame(company);
    let actions = ['meet and greet', 'press event', 'training session', 'charity event', 'merchandise showcase',
        'photo shoot', 'commercial shoot'];

    // Select faction
    const roster = await getCompanyRosterByType(company, 'main');
    let factions = await getRosterActiveFactions(roster);
    if (!factions.length) {
        return null;
    }
    let faction = getRandomChoice(factions);

    // Decrease faction
    let decrease = myround(getRandomInt(-40, -20), 5);
    updateFactionXpAndReputation(faction, decrease);

    // Create message
    let changeHtml = `<div class="message-info-box grey">` +
        `<span class="red margin-right-5"><i class="fa fa-angle-double-down"></i> ${decrease * -1}</span>XP` +
        `</div>`;
    let subject = "Faction Separated";
    let content = `<p>Fans recently saw ${faction.name} attend a ${getRandomChoice(actions)} separately from each other.</p>` +
    `<p>They didn't interact with each other at all, which doesn't bode well for their reputation as a unit.</p>` +
    `<p>${changeHtml}</p>`;
    let talentManager = await getCompanyTalentManager(company);
    const fromName = talentManager ? talentManager.name : 'Staff';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'factionIgnored';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function fluBugEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = await getActiveSuperstars(roster);
    if (superstars.length < 6) {
        return null;
    }
    let numberSuperstarsAffected = getRandomInt(4, 7);
    let superstarsAffected = getRandomSample(superstars, numberSuperstarsAffected);
    let superstarNames = commaSeparate(superstarsAffected.map(x => x.name));

    // Make superstars unusable
    for (let superstar of superstarsAffected) {
        superstar.unavailable = true;
    }

    // Create message from doctor
    let subject = "Flu Bug";
    let content = `<p>A serious flu bug is going around the locker room. So far, the following Superstars have ` +
        `been affected: ${superstarNames}.</p>` +
        `<p>All of them have been sent home and won't be wrestling at the upcoming show. It's a big ` +
        `setback, but we can't let anyone else catch the illness.</p>`;
    let doctor = getRandomStaffMemberByRole(await getCompanyStaff(company), 'doctor');
    let fromName = doctor ? doctor.name : 'Staff';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'fluBug';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function flightsCancelledEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select about half of active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);
    if (superstars.length < 20) {
        return null;
    }
    const halfNumberOfSuperstars = superstars.length / 2;
    const numberOfSuperstarsAffected = getRandomInt(halfNumberOfSuperstars - 5, halfNumberOfSuperstars + 5);
    const superstarsAffected = getRandomSample(superstars, numberOfSuperstarsAffected);

    // Make superstars unusable
    for (let superstar of superstarsAffected) {
        superstar.unavailable = true;
    }

    // Create message from talent manager
    let subject = "Cancelled Flights";
    let content = "<p>We faced some really bad weather coming out of our last show. All flights from the " +
        "airport were cancelled and the weather still hasn't improved.</p>" +
        "<p>Thankfully, around half the Superstars left before the storm and made it here. " +
        "The rest are stuck and won't be able to make it in time for our upcoming show.</p>" +
        "<p>We'll have to work with the talent we have. It might not be ideal but it's a chance to " +
        "get creative.</p>";
    content += '<p>Top names who are unavailable include:</p>';
    const topSuperstarsAffected = [...superstarsAffected].sort((a, b) => b.popularity - a.popularity).slice(0, 5);
    for (let s of topSuperstarsAffected) {
        content += '<p>- ' + s.name + '</p>';
    }
    let talentManager = await getCompanyTalentManager(company);
    let fromName = talentManager ? talentManager.name : 'Staff';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'flightsCancelled';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function championshipStolenEventFunction(company) {
    const game = await getCompanyGame(company);
    const mainCompany = await getGameMainUserCompany(game);

    // Select championship and champion
    let companyChampionships = getUniqueChampionships((await getCompanyChampionships(company)).filter(x =>
        !x.championshipContract && !x.matchContract && !x.tagTeam && !x.trio && !x.vacated
    ));
    if (!companyChampionships.length) {
        return null;
    }
    let championship = getRandomChoice(companyChampionships);
    let champion = await getChampionshipSuperstar(championship);

    // Create random event
    let newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'championshipStolen';
    newRandomEvent.monthsRemaining = 2;
    await addCompanyRandomEvents(company, [newRandomEvent]);
    await addChampionshipRandomEvents(championship, [newRandomEvent]);
    if (champion) {
        await addSuperstarRandomEvents(champion, [newRandomEvent]);
    }

    // Remove championship relationships
    championship.companyId = null;
    championship.superstarId = null;
    championship.matchId = null;
    championship.tournamentId = null;

    // Create message from owner
    let subject = "Championship Stolen";
    let content = `<p>The ${championship.name} Championship is missing. I don't know how this could happen. The ` +
        `champion, ${champion.name}, has no idea where it is and thinks that it was stolen during travel.</p>` +
        `<p>This is an embarrassment! We're going to get the championship back, but for now, you'll have to book ` +
        `without it.</p>`;
    let fromName = company.ownerName || 'Owner';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    await addCompanyMessages(company, [newMessage]);

    // Add news
    let newsItem = await generateNewsItem({
        company, month: game.monthCount, year: game.year, newsType: 'championship_stolen', championship,
    });
    await addCompanyNewsItems(mainCompany, [newsItem]);

    return true;
}

async function backstageUnrestEventFunction(company) {
    const game = await getCompanyGame(company);

    // Select from active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = await getActiveSuperstars(roster);
    let happySuperstars = superstars.filter(x => !x.unhappy);
    let unhappySuperstars = superstars.filter(x => x.unhappy);
    if (happySuperstars.length < 6 || unhappySuperstars.length < 1) {
        return null;
    }
    let superstarsAffected = getRandomSample(happySuperstars, getRandomInt(4, 6));
    let instigatorSuperstar = getRandomChoice(unhappySuperstars);

    // Make superstars unhappy
    if (!game.settings['no_unhappiness']) {
        for (let superstar of superstarsAffected) {
            await changeSuperstarMoraleToUnhappy(superstar, company);
        }
    }

    // Create message from talent manager
    let superstarsAffectedNames = commaSeparate(superstarsAffected.map(x => x.name));
    let subject = "Backstage Unrest";
    let content = `<p>We are dealing with some serious backstage unrest that seems to be spreading. The following ` +
        `Superstars have made their unhappiness clear to me: ${superstarsAffectedNames}.</p>` +
        `<p>I believe all this was started by ${instigatorSuperstar.name} who was unhappy and let everyone know ` +
        `about it.</p>`;
    let talentManager = await getCompanyTalentManager(company);
    let fromName = talentManager ? talentManager.name : 'Staff';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'backstageUnrest';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function conservativeGovernmentShowEventFunction(company) {
    const game = await getCompanyGame(company);
    const locations = ['Saudi Arabia', 'Afghanistan', 'Yemen'];

    // Check for female superstars
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);
    const femaleSuperstars = superstars.filter(x => x.gender === 'female');
    if (femaleSuperstars.length < 7) {
        return null;
    }

    // Get location
    const location = getRandomChoice(locations);

    // Create random event
    let newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'conservativeGovernmentShow';
    newRandomEvent.monthsRemaining = 2;
    newRandomEvent.choices = ['Accept', 'Decline'];
    newRandomEvent.location = location;
    await addCompanyRandomEvents(company, [newRandomEvent]);

    // Create message for random event
    let subject = `${location}'s Offer`;
    let content = `<p>${location}'s government want us to hold an event in their country to promote their sports ` +
        `and entertainment options.</p><p>They're willing to offer us substantial compensation for it. ` +
        `They insisted, however, that women won't be allowed to wrestle, as per their cultural norms.</p>` +
        `<p>If you are willing to do the show, I'll give you plenty of Creative Points to spend. ` +
        `But the fans and some of our female roster might not be happy with it.</p>`;
    let fromName = company.ownerName || 'Owner';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    newMessage.promptAction = true;
    await addRandomEventMessages(newRandomEvent, [newMessage]);

    return true;
}

async function planeRideSuspensionsEventFunction(company) {
    const game = await getCompanyGame(company);
    let pranks = ['razor-shaving prank', 'scare prank', 'laxative prank', 'bathroom prank', 'scissors prank'];
    let fights = ['an amateur wrestling fight', 'a shoot fight', 'a big brawl', 'a boxing contest', 'an MMA fight'];
    let inappropriateActions = ['public nudity', 'bullying staff members', 'threatening the travel crew',
                               'recreational drug use', 'harassing other passengers'];

    // Select active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = await getActiveSuperstars(roster);
    if (superstars.length < 7) {
        return null;
    }
    let superstarsSuspended = getRandomSample(superstars, 5);
    superstars = superstars.filter(x => !objectInArray(x, superstarsSuspended));
    let receivingSuperstar = getRandomChoice(superstars);

    // Suspend superstars
    for (let superstar of superstarsSuspended) {
        superstar.suspensionMonths = 1;

        // Add to company logs
        const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
        await CompanyLogs.insert(newCompanyLog);
        newCompanyLog.text = superstar.name;
        newCompanyLog.type = 'suspension';
        newCompanyLog.value = superstar.suspensionMonths;
        await addCompanyCompanyLogs(company, [newCompanyLog]);

        // Check talent request
        await checkTalentRequestAction({company: company, requestType: 'write_off', talent: superstar});
    }

    // Create message from owner
    let subject = 'Travel Disaster';
    let content = `<p>On the way back from our last show, we had a number of unfortunate incidents. ` +
        `There were too many intoxicants available and the long travel didn't help.</p>` +
        `<p>I won't go into too many details. The following Superstars have been suspended:</p>` +
        `<p>- ${superstarsSuspended[2].name} and ${superstarsSuspended[3].name}, for starting ${getRandomChoice(fights)}.</p>` +
        `<p>- ${superstarsSuspended[0].name} and ${superstarsSuspended[1].name}, for almost injuring ` +
        `${receivingSuperstar.name} in a ${getRandomChoice(pranks)}.</p>` +
        `<p>- ${superstarsSuspended[4].name}, for ${getRandomChoice(inappropriateActions)}.</p>` +
        `<p>I'll make sure this never happens again.</p>`;
    let fromName = company.ownerName || 'Owner';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    let newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'planeRideSuspensions';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function stormAffectsShowEventFunction(company) {
    const game = await getCompanyGame(company);

    // Check event and producers
    let nextEvent = await getCompanyLatestEvent(company);
    if (!nextEvent.weeklyShow) {
        return null;
    }
    let producers = getAvailableStaffMembersByRole(await getCompanyStaff(company), 'producer');
    if (!producers.length) {
        return null;
    }

    // Add active effect to event
    nextEvent.activeEffect = 'production_packages_only';

    // Create message from owner
    let subject = `${nextEvent.name} Cancelled`;
    let content = `<p>Due to a huge storm in the area, we can't hold a live event at this week's ${nextEvent.name}.</p>` +
        `<p>There's a hurricane warning, so there won't be any fans in the arena, and we can't hold any matches. ` +
        `But we can air production packages.</p>` +
        `<p>The show must go on. Use this as an opportunity to build up some Superstars and Feuds.</p>`;
    let fromName = company.ownerName || 'Owner';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    let newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'stormAffectsShow';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function showAppearancesEventFunction(company) {
    const game = await getCompanyGame(company);
    let shows = ['Late Night', 'Talk', 'Comedy', 'Sports', 'Game'];

    // Check if weekly show
    const nextEvent = await getCompanyLatestEvent(company);
    if (!nextEvent.weeklyShow) {
        return null;
    }

    // Get active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = await getActiveSuperstars(roster);
    if (superstars.length < 6) {
        return null;
    }

    // Select superstars using weighted choice (auto booker formula)
    let weights = superstars.map(x => {
        return [x, Math.exp(x.popularity / 70) / 100];
    });
    let superstarsSelected = [];
    for (let _ of Array(getRandomInt(4, 6)).keys()) {
        let selected = weightedChoice(weights);
        weights = weights.filter(x => x[0].$loki !== selected.$loki);
        superstarsSelected.push(selected);
    }

    // Get show name
    let showName = getRandomChoice(shows);

    // Make superstars unavailable, add overness
    for (let s of superstarsSelected) {
        s.unavailable = true;
        let overnessIncrease = getRandomInt(5, 10);
        updateObjectOverness(s, overnessIncrease);
        checkObjectHot(s, overnessIncrease);
        checkObjectStale(s, overnessIncrease);
    }

    // Create message from owner
    let superstarNames = commaSeparate(superstarsSelected.map(x => x.name));
    let subject = `${showName} Show`;
    let content = `<p>We received an offer from a popular ${showName} Show to have some of our Superstars appear on it.</p>` +
        `<p>I selected ${superstarNames} to go.</p>` +
        `<p>The ${showName} Show runs live and airs close to ${nextEvent.name} so they won't be ` +
        `available for our upcoming show, but they should get a nice overness boost.</p>`;
    let fromName = company.ownerName || 'Owner';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    let newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'showAppearances';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    return true;
}

async function backstageFightEventFunction(company) {
    const game = await getCompanyGame(company);
    let moves = ['headlock', 'stiff punch', 'chokehold', 'right hook', 'takedown'];

    // Check feud item
    let feudItem = (await getCompanyFeudItems(company)).find(x => x.requirement === 'backstage-fight');
    if (!feudItem) {
        return null;
    }

    // Get active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = await getActiveSuperstars(roster);
    if (superstars.length < 7) {
        return null;
    }

    // Find superstars with troublemaker trait
    let troublemakerTraitSuperstars = [];
    let backstageFightStarterChance = 0;
    for (let superstar of superstars) {
        for (let trait of superstar.traits) {
            let traitData = traitsData[trait];
            if (traitData && traitData['backstage_fight_starter_chance']) {
                troublemakerTraitSuperstars.push(superstar);
                backstageFightStarterChance = traitData['backstage_fight_starter_chance'];
            }
        }
    }

    // Get fight superstars
    let fightStarter;
    if (troublemakerTraitSuperstars.length && getRandomInt(0, 100) <= backstageFightStarterChance) {
        fightStarter = getRandomChoice(troublemakerTraitSuperstars);
    }
    else {
        fightStarter = getRandomChoice(superstars);
    }
    superstars = superstars.filter(x => x.$loki !== fightStarter.$loki);
    const fightAgainst = getRandomChoice(superstars);
    superstars = superstars.filter(x => x.$loki !== fightAgainst.$loki);
    const fightSuperstars = [fightStarter, fightAgainst];
    const fightingNames = fightSuperstars.map(x => x.name);

    // Get intervening superstars
    let interveningSuperstars = getRandomSample(superstars, getRandomInt(3, 5));

    // Suspend first superstar, make second unhappy
    fightSuperstars[0].suspensionMonths = 1;
    if (!game.settings['no_unhappiness']) {
        await changeSuperstarMoraleToUnhappy(fightSuperstars[1], company);
    }

    // Check talent request
    await checkTalentRequestAction({company: company, requestType: 'write_off', talent: fightSuperstars[0]});

    // Create potential feud
    const newFeud = Feud();
    await Feuds.insert(newFeud);
    newFeud.name = feudItem.name;
    newFeud.hype = feudItem.baseHype;
    newFeud.participants = fightingNames;
    newFeud.directFeudEvent = 'backstage-fight';
    newFeud.startMonth = game.monthCount;
    newFeud.startYear = game.year;
    await addFeudItemFeuds(feudItem, [newFeud]);
    await addRosterFeuds(roster, [newFeud]);
    await addFeudSuperstars(newFeud, fightSuperstars);

    // Create message from owner
    let reasons = [
        `${fightingNames[1]} being unsafe and dangerous to work with`, 'the fallout from an affair',
        `disparaging comments ${fightingNames[1]} made in an interview`,
        `allegations ${fightingNames[1]} made in a book`, 'an unpaid loan'
    ];
    let subject = 'Backstage Fight';
    let content = `<p>We had an unfortunate incident after our last show.</p>` +
        `<p>${fightingNames[0]} got into a disagreement with ${fightingNames[1]} backstage, reportedly ` +
        `about ${getRandomChoice(reasons)}.</p>` +
        `<p>The two started yelling and pushing each other before it came to blows. ${fightingNames[0]}` +
        ` got the better of ${fightingNames[1]} with a ${getRandomChoice(moves)} before ` +
        `${commaSeparate(interveningSuperstars.map(x => x.name))} broke it up.</p>` +
        `<p>Needless to say, things are pretty heated between ${fightingNames[1]} and ${fightingNames[0]}. ` +
        `I have suspended ${fightingNames[0]} for starting the fight while ${fightingNames[1]} is unhappy about ` +
        `the whole thing.</p>` +
        `<p>The only positive you can take out of this is that a Feud between the two would be quite hot.</p>`;
    let fromName = company.ownerName || 'Owner';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'backstageFight';
    await addCompanyRandomEvents(company, [newRandomEvent]);

    // Check to acquire trait
    await checkAcquireTrait(fightStarter, 'Troublemaker');

    return true;
}

async function superstarBreakEventFunction(company) {
    const game = await getCompanyGame(company);

    // Filter out part timers and superstars who have workable injury and want to work through it
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = (await getActiveSuperstars(roster)).filter(x => !x.partTimer &&
        (!x.workableInjury || !checkSuperstarWorkThroughInjury(x)));
    if (!superstars.length) {
        return null;
    }

    // Select superstar
    let weights = [];
    for (let x of superstars) {
        let weight;
        if (game.settings['enable_fatigue'] !== false && x.fatigue !== null) {
            weight = Math.pow(x.fatigue / 20, 3);
            if (x.fatigue >= 60) {
                weight *= 1000;
            }
            else if (x.fatigue >= 40) {
                weight *= 50;
            }
        }
        else {
            weight = Math.pow(x.popularity / 250, 4.5);
        }
        weights.push([x, weight]);
    }
    let superstar = weightedChoice(weights);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'superstarBreak';
    newRandomEvent.monthsRemaining = 1;
    newRandomEvent.choices = ['Accept', 'Decline'];
    await addCompanyRandomEvents(company, [newRandomEvent]);
    await addSuperstarRandomEvents(superstar, [newRandomEvent]);

    // Create message for random event
    let subject = 'Time Off';
    let content = `<p>The past few months have been quite tough on me, both physically and mentally. The long travel ` +
        `and the wrestling really adds up.</p>` +
        `<p>I think I need some time off. Just a few months to recharge and come back fresh. What do you say?</p>` +
        `<p>If you give me this time off, I'll be here till the upcoming monthly show and I'll be happy to do ` +
        `anything on it.</p>`;
    let newMessage = await createMessage(subject, superstar.name, game.monthCount, game.year, content, 'superstar');
    newMessage.promptAction = true;
    await addRandomEventMessages(newRandomEvent, [newMessage]);

    return true;
}

async function drugScandalEventFunction(company) {
    const game = await getCompanyGame(company);

    // Check superstars
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);
    if (superstars.length < 10) {
        return null;
    }

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'drugScandal';
    newRandomEvent.monthsRemaining = 1;
    newRandomEvent.choices = ['Suspend', 'Ignore'];
    await addCompanyRandomEvents(company, [newRandomEvent]);

    // Select superstars
    let selectionRandom = new Math.seedrandom(newRandomEvent.meta.created);
    let numberOfSuperstarsAffected = getRandomInt(5, 10, selectionRandom());
    let superstarsAffected = getRandomSample(superstars, numberOfSuperstarsAffected, selectionRandom);
    let superstarsAffectedNames = commaSeparate(superstarsAffected.map(x => x.name));
    newRandomEvent.selectionIds = superstarsAffected.map(x => x.$loki);

    // Create message from owner
    let subject = "Steroid Scandal";
    let content = `<p>Some of our Superstars are in trouble. Law enforcement agencies identified ` +
        `${numberOfSuperstarsAffected} of our Superstars as part of a recent steroid scandal: ` +
        `${superstarsAffectedNames}.</p>` +
    `<p>The story is growing bigger as we speak. Members of government are looking to investigate the use of ` +
        `steroids in wrestling and are paying close attention to this case.</p>` +
    `<p>We have the option to reduce any possible backlash by suspending all Superstars linked with the scandal.</p>` +
    `<p>We can also choose to ignore it and hope the story dies down. But if it doesn't, the backlash could be huge.</p>`;
    let fromName = company.ownerName || 'Owner';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    newMessage.promptAction = true;
    await addRandomEventMessages(newRandomEvent, [newMessage]);

    return true;
}

/*
Process random event functions
 */

async function processGimmickMatchBoostEvent(randomEvent, company, weeklyShow = null) {
    if (randomEvent.monthsRemaining === 0 && !weeklyShow) {
        let gimmickMatch = await getRandomEventGimmickMatch(randomEvent);

        // Remove boost
        gimmickMatch.ratingBoost -= 0.2;

        return true;
    }

    return null;
}

async function processChampionshipStolenEvent(randomEvent, company, weeklyShow = null) {
    const game = await getCompanyGame(company);
    const mainCompany = await getGameMainUserCompany(game);

    if (randomEvent.monthsRemaining === 1 && !weeklyShow) {
        const championship = await getRandomEventChampionship(randomEvent);
        const champion = await getRandomEventSuperstar(randomEvent);

        const subject = "No Luck";
        const content = `<p>We checked everywhere, from our travel partners to the previous arena, and still there's ` +
            `no sign of the ${championship.name} Championship.</p>` +
            `<p>This is getting out of hand. We need to get the police involved. My gut says that ` +
            `${champion.name} was part of the disappearance.</p>`;

        // Create message from owner
        const fromName = company.ownerName || 'Owner';
        const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
        await addCompanyMessages(company, [newMessage]);

        return true;
    }

    if (randomEvent.monthsRemaining === 0 && !weeklyShow) {
        const championship = await getRandomEventChampionship(randomEvent);
        const champion = await getRandomEventSuperstar(randomEvent);

        // Add championship relationships
        await addCompanyChampionships(company, [championship]);
        if (champion) {
            await addSuperstarChampionships(champion, [championship]);
        }

        const subject = "Championship Found";
        const content = `<p>We finally found the ${championship.name} Championship. It was indeed stolen, and the police ` +
            `tracked down the thief before they could sell it.</p>` +
            `<p>What a relief. ${champion.name} had nothing to do with it and promised to take ` +
            `much better care of it.</p><p>Let's give all our focus to the championship to make up for the ` +
            `months it wasn't here.</p>`;

        // Create message from owner
        const fromName = company.ownerName || 'Owner';
        const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
        await addCompanyMessages(company, [newMessage]);

        // Add news
        let newsItem = await generateNewsItem({
            company, month: game.monthCount, year: game.year, newsType: 'championship_found', championship,
        });
        await addCompanyNewsItems(mainCompany, [newsItem]);

        return true;
    }

    return null;
}

async function processDrugAbuseEvent(randomEvent, company) {
    const game = await getCompanyGame(company);
    const mainCompany = await getGameMainUserCompany(game);
    const superstar = await getRandomEventSuperstar(randomEvent);

    // Chance to lower a skill, otherwise stop drug use
    if (randomEvent.monthsRemaining === 0 && randomEvent.selectedChoice === 'Ignore') {
        const pick = getRandomInt(0, 100);
        let subject;
        let content;

        if (pick <= 70) {
            // Lower a skill
            const skillToLower = getRandomChoice(['workrate', 'charisma']);
            const decrease = myround(getRandomInt(50, 80), 5);
            if (skillToLower === 'workrate') {
                superstar.workrateXp -= decrease;
                if (superstar.workrateXp < 0) {
                    superstar.workrateXp = 0;
                }
            }
            else if (skillToLower === 'charisma') {
                superstar.charismaXp -= decrease;
                if (superstar.charismaXp < 0) {
                    superstar.charismaXp = 0;
                }
            }
            setSuperstarSkillLevelForDecrease(superstar, skillToLower);

            const changeHtml = `<div class="message-info-box grey">` +
                `<span class="red margin-right-5"><i class="fa fa-angle-double-down"></i> ${decrease}</span>` +
                `${toTitleCase(skillToLower)} XP` +
                `</div>`;
            subject = "Skill Affected";
            content = `<p>The drug abuse caught up to ${superstar.name}. The effects were quite bad, so much so that I think ` +
                `${superstar.possessivePronoun} ${toTitleCase(skillToLower)} skill was affected.</p>` +
                `<p>${superstar.name} has resolved to not let ${superstar.objectivePronoun}self deteriorate any further. ` +
                `I don't think ${superstar.pronoun} will be using anymore.</p>` +
                `<p>${changeHtml}</p>`;
        }
        else {
            // Drug use stopped
            subject = "Drug Use Ended";
            content = `<p>${superstar.name} hit an all-time low following the drug abuse. There was a lot of shame and guilt, ` +
                `but with the help of friends and family, ${superstar.pronoun} managed to stop using.</p>` +
                `<p>I don't think any significant harm came to ${superstar.objectivePronoun}.</p>`;
        }

        // Create message
        const talentManager = await getCompanyTalentManager(company);
        const fromName = talentManager ? talentManager.name : 'Staff';
        const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
        await addCompanyMessages(company, [newMessage]);

        return true;
    }

    // Suspend superstar for 2 months
    if (randomEvent.selectedChoice === 'Suspend' && !randomEvent.eventsRun.includes('0A')) {
        superstar.suspensionMonths = 2;

        // Check talent request
        await checkTalentRequestAction({company: company, requestType: 'write_off', talent: superstar});

        // Add to company logs
        const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
        await CompanyLogs.insert(newCompanyLog);
        newCompanyLog.text = superstar.name;
        newCompanyLog.type = 'suspension';
        newCompanyLog.value = superstar.suspensionMonths;
        await addCompanyCompanyLogs(company, [newCompanyLog]);

        // Create message
        const subject = "Suspended";
        const content = `<p>${superstar.name} has been suspended for ${superstar.suspensionMonths} months due to drug abuse.</p>` +
            `<p>${toTitleCase(superstar.pronoun)} isn't doing so well but is getting professional help. ` +
            `I believe ${superstar.pronoun} should come back clean and ready to wrestle.</p>`;
        const talentManager = await getCompanyTalentManager(company);
        const fromName = talentManager ? talentManager.name : 'Staff';
        const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
        await addCompanyMessages(company, [newMessage]);

        // Add to events run
        randomEvent.eventsRun.push('0A');

        // Add news
        let newsItem = await generateNewsItem({
            company, month: game.monthCount, year: game.year, newsType: 'drug_use_suspension', superstar,
        });
        await addCompanyNewsItems(mainCompany, [newsItem]);

        return true;
    }

    return null
}

async function processDrugScandalEvent(randomEvent, company) {
    const game = await getCompanyGame(company);

    if (randomEvent.monthsRemaining === 0 && randomEvent.selectedChoice === 'Ignore') {
        const pick = getRandomInt(0, 100);

        // Scandal grows
        if (pick <= 70) {

            // Lower fans
            const decrease = Math.abs(Math.floor(company.fans * 0.2));
            company.fans -= decrease;

            const changeHtml = `<div class="message-info-box grey">` +
                `<span class="red margin-right-5"><i class="fa fa-angle-double-down"></i> ${decrease.toLocaleString('en')}</span>` +
                `Fans` +
                `</div>`;
            const subject = "Scandal Backlash";
            const content = `<p>The steroid scandal has spread to the mainstream audience.</p>` +
                `<p>Media outlets are attacking us for not punishing the Superstars involved and many fans have started ` +
                `boycotts of our product.</p>` +
                `<p>${changeHtml}</p>`;

            // Create message
            const fromName = company.ownerName || 'Owner';
            const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
            await addCompanyMessages(company, [newMessage]);
        }

        // Scandal dies down
        else {
            const subject = "Scandal Over";
            const content = `<p>The steroid scandal has died down in the media. People have moved on to more important ` +
                `things.</p>` +
                `<p>I've given a warning to all the Superstars involved in it. I think that's enough. ` +
                `Suspending them would have been too much.</p>`;

            // Create message
            const fromName = company.ownerName || 'Owner';
            const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
            await addCompanyMessages(company, [newMessage]);
        }

        return true;
    }

    // Suspend superstars
    if (randomEvent.selectedChoice === 'Suspend' && !randomEvent.eventsRun.includes('0A')) {

        // Get superstars
        const roster = await getCompanyRosterByType(company, 'main');
        const superstars = await getActiveSuperstars(roster);

        // Get previously selected superstars
        let superstarsAffected;
        if (randomEvent.selectionIds.length) {
            superstarsAffected = superstars.filter(x => randomEvent.selectionIds.includes(x.$loki));
        }
        // Fallback in case no superstars previously set
        else {
            const selectionRandom = new Math.seedrandom(randomEvent.meta.created);
            const numberOfSuperstarsAffected = getRandomInt(5, 10, selectionRandom());
            superstarsAffected = getRandomSample(superstars, numberOfSuperstarsAffected, selectionRandom);
        }

        // Suspend superstars
        for (const superstar of superstarsAffected) {
            superstar.suspensionMonths = 1;

            // Check talent request
            await checkTalentRequestAction({company: company, requestType: 'write_off', talent: superstar});

            // Add to company logs
            const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
            await CompanyLogs.insert(newCompanyLog);
            newCompanyLog.text = superstar.name;
            newCompanyLog.type = 'suspension';
            newCompanyLog.value = superstar.suspensionMonths;
            await addCompanyCompanyLogs(company, [newCompanyLog]);
        }

        // Create message
        const superstarsAffectedNames = commaSeparate(superstarsAffected.map(x => x.name));
        const subject = "Superstars Suspended";
        const content = `<p>The following Superstars have been suspended for being part of the recent steroid scandal:</p>` +
            `<p>${superstarsAffectedNames}.</p>` +
            `<p>This step should protect us from any fan backlash.</p>`;
        const talentManager = await getCompanyTalentManager(company);
        const fromName = talentManager ? talentManager.name : 'Staff';
        const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
        await addCompanyMessages(company, [newMessage]);

        // Add to events run
        randomEvent.eventsRun.push('0A');

        return true;
    }

    return null;
}

async function processSuperstarBreakEvent(randomEvent, company) {
    const game = await getCompanyGame(company);
    const mainCompany = await getGameMainUserCompany(game);
    const superstar = await getRandomEventSuperstar(randomEvent);

    try {
        if (randomEvent.monthsRemaining === 0 && randomEvent.selectedChoice === 'Accept') {
            // Make happy, unavailable
            changeSuperstarMoraleToHappy(superstar);
            addKayfabeInjuryToSuperstar(superstar, getRandomInt(2, 3));

            // Create message from superstar
            const subject = "Thank you";
            const content = "<p>I really appreciate you giving me time off. I'll be ready to return soon, " +
                "better than ever.</p>";
            const newMessage = await createMessage(subject, superstar.name, game.monthCount, game.year,
                content, 'superstar');
            await addCompanyMessages(company, [newMessage]);

            // Add news
            let newsItem = await generateNewsItem({
                company, month: game.monthCount, year: game.year, newsType: 'superstar_time_off', superstar,
            });
            await addCompanyNewsItems(mainCompany, [newsItem]);

            return true;
        }

        if (randomEvent.selectedChoice === 'Decline' && !randomEvent.eventsRun.includes('0B')) {
            // Make unhappy
            if (!game.settings['no_unhappiness']) {
                await changeSuperstarMoraleToUnhappy(superstar, company);
            }

            // Create message from superstar
            const subject = "Disappointed";
            const content = "<p>I can't believe you wouldn't give me a few months off, despite everything I do for " +
                "you!</p><p>This is complete neglect of your Superstars' health.</p>";
            const newMessage = await createMessage(subject, superstar.name, game.monthCount, game.year,
                content, 'superstar');
            await addCompanyMessages(company, [newMessage]);

            // Add to events run
            randomEvent.eventsRun.push('0B');

            return true;
        }
    } catch (error) {
        captureError(error)
    }
    return null;
}

async function processConservativeGovernmentShowEvent(randomEvent, company, weeklyShow = null) {
    const game = await getCompanyGame(company);
    const eventNames = ['Super Showoff', 'Hurt In The Desert', 'Crowning Point', 'Big Progress', 'Eternal Kingdom'];

    if (randomEvent.selectedChoice === 'Accept') {
        const nextEvent = await getCompanyLatestEvent(company);

        if (randomEvent.monthsRemaining === 1) {

            // Get seeded random event name
            const latestMonthlyEvent = (await getCompanyEvents(company))
                .sort((a, b) => b.$loki - a.$loki)
                .find(x => !x.weeklyShow && x.name !== 'Weekly Show' && x.$loki !== nextEvent.$loki && !x.temporaryEvent);
            const eventRandom = new Math.seedrandom(latestMonthlyEvent.meta.created);
            const eventName = getRandomChoice(eventNames, eventRandom());

            // Add no women bookable effect to monthly event and rename
            if (!nextEvent.weeklyShow) {
                nextEvent.activeEffect = 'no_women_bookable';
                nextEvent.name = eventName;
            }
            else {
                // Change name of weekly show before event
                if (nextEvent.weekNumber < 0) {
                    nextEvent.monthlyEventName = eventName;
                }
            }

            // Run at monthly show or weekly show before monthly show
            if (!nextEvent.weeklyShow || (nextEvent.weeklyShow && nextEvent.weekNumber < 0)) {

                // Run once
                if (!randomEvent.eventsRun.includes('1A')) {

                    // Add points
                    const increase = myround(getRandomInt(400, 600), 50);
                    company.points += increase;

                    // Create message from owner
                    const changeHtml = `<div class="message-info-box blue">` +
                        `<span class="margin-right-5 top--2px">` +
                        `<i class="fa fa-angle-double-up"></i> ${increase}` +
                        `</span>` +
                        `<img alt="Points" src="static/cp_icon.png" class="points-image-small margin-left-5 three-px"/>` +
                        `</div>`;
                    const subject = `${randomEvent.location} Show`;
                    const content = `<p>Now that you signed off on the ${randomEvent.location} show, we're going to ` +
                        `run it this month. The event has been named '${eventName}'.</p>` +
                        `<p>I've given you quite a bit of Creative Points. There will be some fan backlash but ` +
                        `you need to focus on putting on a good show for the ${randomEvent.location} government.</p>` +
                        `<p>${changeHtml}</p>`;
                    const fromName = company.ownerName || 'Owner';
                    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
                    await addCompanyMessages(company, [newMessage]);

                    // Add to events run
                    randomEvent.eventsRun.push('1A');

                    return true;
                }
            }
        }

        // Run after monthly show
        if (randomEvent.monthsRemaining === 0 && !weeklyShow) {
            // Reduce fans
            const decrease = Math.abs(Math.floor(company.fans * 0.2));
            company.fans -= decrease;

            // Create womenOnlyShow random event
            const newRandomEvent = RandomEvent();
            RandomEvents.insert(newRandomEvent);
            newRandomEvent.name = 'womenOnlyShow';
            newRandomEvent.monthsRemaining = 2;
            newRandomEvent.choices = ['Accept', 'Decline'];
            await addCompanyRandomEvents(company, [newRandomEvent]);

            // Create message from owner
            const changeHtml = `<div class="message-info-box grey">` +
                `<span class="red margin-right-5">` +
                    `<i class="fa fa-angle-double-down"></i> ${decrease.toLocaleString('en')}` +
                `</span>` +
                `Fans` +
            `</div>`;
            const subject = 'Backlash Counter';
            const content = `<p>I was expecting some backlash for our ${randomEvent.location} show, but the media and ` +
                `the fans are going crazy. We need to do something drastic to counter this.</p>` +
                `<p>I came up with the idea of holding a women-only show. The fans will like it and so will the female roster. ` +
                `Plus, it'll improve the damage to our image. What do you say?</p>` +
                `<p>${changeHtml}</p>`;
            const fromName = company.ownerName || 'Owner';
            const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
            newMessage.promptAction = true;
            await addRandomEventMessages(newRandomEvent, [newMessage]);

            return true;
        }
    }

    return null;
}

async function processWomenOnlyShowEvent(randomEvent, company, weeklyShow = null) {
    const game = await getCompanyGame(company);

    if (randomEvent.selectedChoice === 'Accept') {
        const nextEvent = await getCompanyLatestEvent(company)

        if (randomEvent.monthsRemaining === 1) {

            // Add no men bookable effect to monthly event
            if (!nextEvent.weeklyShow) {
                nextEvent.activeEffect = 'no_men_bookable';
            }

            // Run at monthly show or weekly show before monthly show
            if (!nextEvent.weeklyShow || (nextEvent.weeklyShow && nextEvent.weekNumber < 0)) {

                // Run once
                if (!randomEvent.eventsRun.includes('0A')) {

                    // Create message from owner
                    const subject = "Women's Show";
                    const content = "<p>We're ready to run our women-only show this month! The roster is excited. " +
                        "Everyone thinks this was a long time coming.</p><p>Let's give the fans " +
                        "a great event and show them that we support our women.</p>";
                    const fromName = company.ownerName || 'Owner';
                    const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
                    await addCompanyMessages(company, [newMessage]);

                    // Add to events run
                    randomEvent.eventsRun.push('0A');

                    return true;
                }
            }
        }

        // Run after monthly show
        if (randomEvent.monthsRemaining === 0 && !weeklyShow) {

            // Increase fans
            const increase = Math.abs(Math.floor(company.fans * 0.2));
            company.fans += increase;

            // Create message from owner
            const changeHtml = `<div class="message-info-box grey">` +
               `<span class="green margin-right-5">` +
                    `<i class="fa fa-angle-double-up"></i> ${increase.toLocaleString('en')}` +
                `</span>` +
                `Fans` +
            `</div>`;
            const subject = "Show Success";
            const content = "<p>Our women-only show was a success! The fans loved seeing the women perform and the media " +
                "ate it up.</p>" +
                "<p>We took a big step towards improving our image and made our brand " +
                "stronger.</p>" +
                `<p>${changeHtml}</p>`;
            const fromName = company.ownerName || 'Owner';
            const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
            await addCompanyMessages(company, [newMessage]);

            return true;
        }
    }

    return null;
}

async function processCultFollowingEvent(randomEvent, company) {
    const game = await getCompanyGame(company);
    const superstar = await getRandomEventSuperstar(randomEvent);

    if (randomEvent.monthsRemaining === 0) {
        const pick = getRandomInt(0, 100);

        if (pick <= 50) {
            // Increase overness, make hot
            let increase;
            if (superstar.overness >= 80) {
                increase = 10;
                superstar.overness += increase;
                if (superstar.overness > 100) {
                    superstar.overness = 100;
                }
            }
            else {
                increase = 80 - superstar.overness;
                superstar.overness = 80;
                superstar.hot = true;
            }

            // Create message
            const changeHtml = `<div class="message-info-box grey">` +
                `<span class="green margin-right-5">` +
                    `<i class="fa fa-angle-double-up"></i> ${increase}%` +
                `</span>` +
                `Overness` +
            `</div>`;
            const subject = "Following Grows";
            const content = `<p>The cult following around ${superstar.name} seems to have grown into a full-on fad.</p>` +
                `<p>${superstar.name} got a big boost to ${superstar.possessivePronoun} overness and is getting quite hot because of this.</p>` +
                `<p>${changeHtml}</p>`;
            const talentManager = await getCompanyTalentManager(company);
            const fromName = talentManager.name || 'Staff';
            const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
            await addCompanyMessages(company, [newMessage]);
        }

        else {
            // Decrease overness, make stale
            let decrease;
            if (superstar.overness < 20) {
                decrease = 10;
                superstar.overness -= decrease;
                if (superstar.overness < 0) {
                    superstar.overness = 0;
                }
            }
            else {
                decrease = superstar.overness - 19;
                superstar.overness = 19;
                superstar.stale = true;
            }

            // Create message
            const changeHtml = `<div class="message-info-box grey">` +
                `<span class="red margin-right-5">` +
                    `<i class="fa fa-angle-double-down"></i> ${decrease}%` +
                `</span>` +
                `Overness` +
            `</div>`;
            const subject = "Following Over";
            const content = `<p>The cult following around ${superstar.name} has turned into an over-used and boring joke. Needless to say, the original interest has died down.</p>` +
                `<p>${superstar.name} got a significant hit to ${superstar.possessivePronoun} overness and is getting quite stale because of it.</p>` +
                `<p>${changeHtml}</p>`;
            const talentManager = await getCompanyTalentManager(company);
            const fromName = talentManager.name || 'Staff';
            const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
            await addCompanyMessages(company, [newMessage]);
        }
    }
    return true;
}

async function backstageBrawlEventFunction(company) {
    const game = await getCompanyGame(company);
    const lastEvent = await getLatestMonthlyEvent((await getCompanyEvents(company)));

    // Get active superstars
    const roster = await getCompanyRosterByType(company, 'main');
    let superstars = await getActiveSuperstars(roster);
    if (superstars.length < 8) {
        return null;
    }

    // Get fight starter weights
    let fightStarterWeights = [];
    for (let x of superstars) {
        let weight = 1;
        if (getRankFromPopularity(x.popularity) === 'Main Eventer') {
            weight += 20
        }
        if (['Troublemaker', 'Rebel', 'Extreme Emotions'].some(y => x.traits.includes(y))) {
            weight += 50
        }
        fightStarterWeights.push([x, weight]);
    }

    // Get fight starter
    let fightStarter;
    if (fightStarterWeights.length) {
        fightStarter = weightedChoice(fightStarterWeights);
        fightStarterWeights = fightStarterWeights.filter(x => x[0].$loki !== fightStarter.$loki);
    }
    else {
        fightStarter = getRandomChoice(superstars);
    }
    superstars = superstars.filter(x => x.$loki !== fightStarter.$loki);

    // Adjust fight starter weights based on gender
    let newFightStarterWeights = fightStarterWeights.filter(x => x[0].gender === fightStarter.gender);
    if (newFightStarterWeights.length) {
        fightStarterWeights = newFightStarterWeights
    }

    // Get fight starter partners
    let fightStarterPartners = [];
    let fightStarterTagTeams = (await getSuperstarTagTeams(fightStarter)).filter(x => !x.temporary);
    let fightStarterFactions = await getSuperstarActiveFactions(fightStarter);
    for (let x of fightStarterTagTeams.concat(fightStarterFactions)) {
        fightStarterPartners.push(...(await getRosterMemberSuperstars(x)).filter(x => x.$loki !== fightStarter.$loki))
    }

    // Get fight starter helpers
    let fightStarterHelpers = [];
    for (let x of Array(getRandomInt(2, 3)).keys()) {
        let fightStarterHelper;
        if (fightStarterPartners.length && getRandomInt(0, 100) <= 75) {
            fightStarterHelper = getRandomChoice(fightStarterPartners)
            fightStarterPartners = fightStarterPartners.filter(x => x.$loki !== fightStarterHelper.$loki);
        }
        else {
            if (fightStarterWeights.length) {
                fightStarterHelper = weightedChoice(fightStarterWeights);
                fightStarterWeights = fightStarterWeights.filter(y => y[0].$loki !== fightStarterHelper.$loki);
            }
            else {
                fightStarterHelper = getRandomChoice(superstars);
            }
        }
        fightStarterHelpers.push(fightStarterHelper);
        superstars = superstars.filter(x => x.$loki !== fightStarterHelper.$loki);
    }

    // Get fight against weights
    let fightAgainstWeights = [];
    for (let x of superstars) {
        if (x.gender === fightStarter.gender) {
            let weight = 1;
            if (getRankFromPopularity(x.popularity) === 'Main Eventer') {
                weight += 20
            }
            if (x.traits.includes('Loyal', 'Giving Veteran', 'Leader')) {
                weight += 50
            }
            fightAgainstWeights.push([x, weight]);
        }
    }

    // Get fight against
    let fightAgainstSuperstar;
    if (fightAgainstWeights.length) {
        fightAgainstSuperstar = weightedChoice(fightAgainstWeights)
        fightAgainstWeights = fightAgainstWeights.filter(x => x[0].$loki !== fightAgainstSuperstar.$loki);
    }
    else {
        fightAgainstSuperstar = getRandomChoice(superstars);
    }
    superstars = superstars.filter(x => x.$loki !== fightAgainstSuperstar.$loki);

    // Get fight against partners
    let fightAgainstPartners = [];
    let fightAgainstTagTeams = (await getSuperstarTagTeams(fightAgainstSuperstar)).filter(x => !x.temporary);
    let fightAgainstFactions = await getSuperstarActiveFactions(fightAgainstSuperstar);
    for (let x of fightAgainstTagTeams.concat(fightAgainstFactions)) {
        fightAgainstPartners.push(...(await getRosterMemberSuperstars(x)).filter(x => x.$loki !== fightAgainstSuperstar.$loki))
    }

    // Get fight against helpers
    let fightAgainstHelpers = [];
    for (let x of Array(getRandomInt(2, 3)).keys()) {
        let fightAgainstHelper;
        if (fightAgainstPartners.length && getRandomInt(0, 100) <= 75) {
            fightAgainstHelper = getRandomChoice(fightAgainstPartners)
            fightAgainstPartners = fightAgainstPartners.filter(x => x.$loki !== fightAgainstHelper.$loki);
        }
        else {
            if (fightAgainstWeights.length) {
                fightAgainstHelper = weightedChoice(fightAgainstWeights);
                fightAgainstWeights = fightAgainstWeights.filter(y => y[0].$loki !== fightAgainstHelper.$loki);
            }
            else {
                fightAgainstHelper = getRandomChoice(superstars);
            }
        }
        fightAgainstHelpers.push(fightAgainstHelper);
        superstars = superstars.filter(x => x.$loki !== fightAgainstHelper.$loki);
    }

    // Create message from owner
    const fightSuperstars = [fightStarter, ...fightStarterHelpers, fightAgainstSuperstar, ...fightAgainstHelpers];
    let reasons = [
        `circulating dirt sheet rumors`,
        `allegations during a post event interview`,
        `refusing to take advice`
    ];
    let severeAction = [
        `${getRandomChoice(fightSuperstars).name} got knocked out`,
        `a table got thrown at ${getRandomChoice(fightSuperstars).name}`,
        `${getRandomChoice(fightSuperstars).name}'s valuables got destroyed`
    ];
    let subject = 'Backstage Brawl';
    let content = `<p>After ${lastEvent.name}, ${fightStarter.name} got into a disagreement with ` +
        `${fightAgainstSuperstar.name} in the locker room, reportedly about ${getRandomChoice(reasons)}.</p>` +
        `<p>As the argument got heated, ${commaSeparate(fightAgainstHelpers.map(x => x.name))} joined ` +
        `${fightAgainstSuperstar.name}, which only escalated things.</p>` +
        `<p>${commaSeparate(fightStarterHelpers.map(x => x.name))} rushed to defend ${fightStarter.name}, and a brawl ` +
        `broke out between the two parties in which ${getRandomChoice(severeAction)}.</p>` +
        `<p>Tensions are high backstage. We don't know who started the fight, so the safe choice would be ` +
        `to suspend everyone involved in the brawl. We could also ignore it and hope things fix themselves.</p>`;
    let fromName = company.ownerName || 'Owner';
    let newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'owner');
    newMessage.promptAction = true;
    await addCompanyMessages(company, [newMessage]);

    // Create random event
    const newRandomEvent = RandomEvent();
    RandomEvents.insert(newRandomEvent);
    newRandomEvent.name = 'backstageBrawl';
    await addCompanyRandomEvents(company, [newRandomEvent]);
    newRandomEvent.monthsRemaining = 1;
    newRandomEvent.choices = ['Suspend', 'Ignore'];
    newRandomEvent.selectionIds = fightSuperstars.map(x => x.$loki);
    await addRandomEventMessages(newRandomEvent, [newMessage]);

    // Check to acquire trait
    await checkAcquireTrait(fightStarter, 'Troublemaker');

    return true;
}

async function processBackstageBrawlEvent(randomEvent, company) {
    const game = await getCompanyGame(company);

    // Get superstars
    const roster = await getCompanyRosterByType(company, 'main');
    const superstars = await getActiveSuperstars(roster);
    const superstarsAffected = superstars.filter(x => randomEvent.selectionIds.includes(x.$loki));
    const superstarsAffectedNames = commaSeparate(superstarsAffected.map(x => x.name));

    if (randomEvent.monthsRemaining === 0 && randomEvent.selectedChoice === 'Ignore') {
        const pick = getRandomInt(0, 100);
        let subject;
        let content;

        if (pick <= 70) {
            // Make some superstars unhappy
            let superstarsToMakeUnhappy = getRandomSample(superstarsAffected, Math.ceil(superstarsAffected.length / 2));
            for (let x of superstarsToMakeUnhappy) {
                if (!game.settings['no_unhappiness']) {
                    await changeSuperstarMoraleToUnhappy(x, company);
                }
            }
            subject = "Unresolved issues";
            content = `<p>The issues resulting from the backstage brawl haven't improved; in fact ` +
                `${commaSeparate(superstarsToMakeUnhappy.map(x => x.name))} are clearly unhappy with the situation ` +
                `and their morale has dropped.</p>` +
                `<p>I think our lack of dealing with the issue didn't help.</p>`;
        }
        else {
            subject = "Issues resolved";
            content = `<p>The issues between ${superstarsAffectedNames} seem to have been resolved.</p>` +
                `<p>I think the brawl has largely been forgotten and everyone is eager to work together.</p>`;
        }

        // Create message
        const talentManager = await getCompanyTalentManager(company);
        const fromName = talentManager ? talentManager.name : 'Staff';
        const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
        await addCompanyMessages(company, [newMessage]);

        return true;
    }

    // Suspend superstars
    if (randomEvent.selectedChoice === 'Suspend' && !randomEvent.eventsRun.includes('0A')) {

        for (const superstar of superstarsAffected) {
            superstar.suspensionMonths = 1;

            // Check talent request
            await checkTalentRequestAction({company: company, requestType: 'write_off', talent: superstar});

            // Add to company logs
            const newCompanyLog = CompanyLog(game.monthCount, company.realYear);
            await CompanyLogs.insert(newCompanyLog);
            newCompanyLog.text = superstar.name;
            newCompanyLog.type = 'suspension';
            newCompanyLog.value = superstar.suspensionMonths;
            await addCompanyCompanyLogs(company, [newCompanyLog]);
        }

        // Create message
        const subject = "Superstars Suspended";
        const content = `<p>The following superstars involved in the recent backstage brawl have been suspended:</p>` +
            `<p>${superstarsAffectedNames}.</p>` +
            `<p>This should help protect the locker room's morale.</p>`;
        const talentManager = await getCompanyTalentManager(company);
        const fromName = talentManager ? talentManager.name : 'Staff';
        const newMessage = await createMessage(subject, fromName, game.monthCount, game.year, content, 'staff');
        await addCompanyMessages(company, [newMessage]);

        // Add to events run
        randomEvent.eventsRun.push('0A');

        return true;
    }

    return null;
}

export {checkForStaffRandomEvent, randomEventCheck, getRandomEventData, addRandomEventChoice}
